import { createBrowserRouter, Navigate, Outlet } from "react-router-dom";
import { lazy, Suspense } from "react";
import AppLayout from "./layouts/AppLayout";

// Lazy-load pages for code splitting
const LandingPage = lazy(() => import("./pages/public/LandingPage"));
const LoginPage = lazy(() => import("./pages/public/LoginPage"));
const RegisterPage = lazy(() => import("./pages/public/RegisterPage"));
const ForgotPasswordPage = lazy(() => import("./pages/public/ForgotPasswordPage"));
const SchedulePage = lazy(() => import("./pages/public/SchedulePage"));

const DashboardPage = lazy(() => import("./pages/DashboardPage"));
const LeadsPage = lazy(() => import("./pages/leads/LeadsPage"));
const LeadDetailPage = lazy(() => import("./pages/leads/LeadDetailPage"));
const AppointmentsPage = lazy(() => import("./pages/appointments/AppointmentsPage"));
const AppointmentDetailPage = lazy(() => import("./pages/appointments/AppointmentDetailPage"));
const FollowUpsPage = lazy(() => import("./pages/FollowUpsPage"));
const TodosPage = lazy(() => import("./pages/TodosPage"));

const NotificationsPage = lazy(() => import("./pages/communications/NotificationsPage"));
const ProvidersPage = lazy(() => import("./pages/communications/ProvidersPage"));
const TemplatesPage = lazy(() => import("./pages/communications/TemplatesPage"));

const UsersPage = lazy(() => import("./pages/admin/UsersPage"));
const ConfigPage = lazy(() => import("./pages/admin/config/ConfigPage"));
const QualificationConfigPage = lazy(() => import("./pages/admin/config/QualificationConfigPage"));
const CallScriptPage = lazy(() => import("./pages/admin/config/CallScriptPage"));
const ClientDatabasePage = lazy(() => import("./pages/admin/config/ClientDatabasePage"));
const AuditPage = lazy(() => import("./pages/admin/AuditPage"));
const AgentsPage = lazy(() => import("./pages/admin/AgentsPage"));
const AssignmentRulesPage = lazy(() => import("./pages/admin/AssignmentRulesPage"));
const TestLeadPage = lazy(() => import("./pages/admin/TestLeadPage"));
const DocsPage = lazy(() => import("./pages/docs/DocsPage"));
const RoboAdvisorPage = lazy(() => import("./pages/ai/RoboAdvisorPage"));

function SuspenseWrapper() {
  return (
    <Suspense
      fallback={
        <div className="flex h-screen items-center justify-center">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
        </div>
      }
    >
      <Outlet />
    </Suspense>
  );
}

export const router = createBrowserRouter([
  {
    element: <SuspenseWrapper />,
    children: [
      // Public routes
      { path: "/", element: <Navigate to="/login" replace /> },
      { path: "/login", element: <LoginPage /> },
      { path: "/register", element: <RegisterPage /> },
      { path: "/forgot-password", element: <ForgotPasswordPage /> },
      { path: "/schedule/:token", element: <SchedulePage /> },

      // Authenticated app routes
      {
        element: <AppLayout />,
        children: [
          { path: "/dashboard", element: <DashboardPage /> },
          { path: "/leads", element: <LeadsPage /> },
          { path: "/leads/:id", element: <LeadDetailPage /> },
          { path: "/appointments", element: <AppointmentsPage /> },
          { path: "/appointments/:id", element: <AppointmentDetailPage /> },
          { path: "/follow-ups", element: <FollowUpsPage /> },
          { path: "/todos", element: <TodosPage /> },
          { path: "/communications/notifications", element: <NotificationsPage /> },
          { path: "/communications/providers", element: <ProvidersPage /> },
          { path: "/communications/templates", element: <TemplatesPage /> },
          { path: "/admin/users", element: <UsersPage /> },
          { path: "/admin/config", element: <ConfigPage /> },
          { path: "/admin/config/qualification", element: <QualificationConfigPage /> },
          { path: "/admin/config/call-script", element: <CallScriptPage /> },
          { path: "/admin/config/client-database", element: <ClientDatabasePage /> },
          { path: "/admin/audit", element: <AuditPage /> },
          { path: "/admin/agents", element: <AgentsPage /> },
          { path: "/admin/assignment-rules", element: <AssignmentRulesPage /> },
          { path: "/admin/test-lead", element: <TestLeadPage /> },
        ],
      },

      // Full-page views (authenticated but no sidebar)
      { path: "/robo-advisor/:leadId", element: <RoboAdvisorPage /> },
      { path: "/docs", element: <DocsPage /> },

      // Catch-all redirect
      { path: "*", element: <Navigate to="/dashboard" replace /> },
    ],
  },
]);
